// ** Modules
import React from 'react';
import { signIn } from 'next-auth/react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';
import ArrowSlideHoverCta from '@/components/common/ArrowSlideHoverCta';
import { Icon } from '@/components/common/icons';

// ** Utils
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { StrapiComponentElementProps } from '@/types/strapi';
import type { ComponentElementsButtonLinkCard } from '@/types/graphql';

export type LinkRowCardItemProps = StrapiComponentElementProps<ComponentElementsButtonLinkCard>;

const PRIMARY_GLYPH_ICONS = new Set(['graph', 'table', 'dashboard']);

const LinkRowCard = ({
	title,
	providers,
	buttonLinkRowCardLink,
	description,
	buttonLinkRowCardIcon,
	buttonLinkRowCardIsLogin
}: LinkRowCardItemProps) => {
	const theme = useTheme();
	const [isCardHovered, setIsCardHovered] = React.useState(false);
	const externalLink = buttonLinkRowCardLink?.hrefExternal;
	const link = !externalLink ? buildPathFromRelationLink(buttonLinkRowCardLink?.href) : externalLink;

	const isExternal = !!externalLink;
	const parseIcon = typeof buttonLinkRowCardIcon === 'object' ? buttonLinkRowCardIcon?.icon : buttonLinkRowCardIcon;
	const iconName = parseIcon?.trim();
	const glyphColor = PRIMARY_GLYPH_ICONS.has(iconName ?? '') ? theme.palette.primary.main : theme.palette.common.white;

	const handleLogin = (e: React.MouseEvent) => {
		if (buttonLinkRowCardIsLogin) {
			e.preventDefault();
			e.stopPropagation();

			const providerData = providers as any;
			const providerId = providerData?.prototype?.id;

			if (providerId) {
				signIn(providerId);
			}
		}
	};

	return (
		<Link
			passHref
			fullWidth
			href={buttonLinkRowCardIsLogin ? '#' : link || '#'}
			target={isExternal ? '_blank' : undefined}
			onClick={handleLogin}
			style={{ textDecoration: 'none', height: '100%', display: 'block' }}
		>
			<Box
				className="linkrow-card"
				onMouseEnter={() => setIsCardHovered(true)}
				onMouseLeave={() => setIsCardHovered(false)}
				sx={(t) => ({
					bgcolor: 'common.white',
					borderRadius: 2,
					px: { xs: 4, md: 5 },
					py: { xs: 5, md: 6 },
					minHeight: { xs: 160, sm: 216 },
					height: 'auto',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '1.5rem',
					position: 'relative',
					overflow: 'hidden',
					transition: 'box-shadow 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
					[t.breakpoints.up('md')]: {
						'@media (hover: hover)': {
							'&:hover': {
								boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
								'& .linkrow-default': { opacity: 0 },
								'& .linkrow-hover': {
									opacity: 1,
									transform: 'translateY(0)'
								}
							}
						}
					}
				})}
			>
				{/* Default state: icon + title */}
				<Box
					className="linkrow-default"
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '1.5rem',
						width: '100%',
						minWidth: 0,
						boxSizing: 'border-box',
						transition: 'opacity 0.4s ease',
						...(description
							? {
									position: 'absolute',
									inset: 0,
									px: { xs: 4, md: 5 },
									py: { xs: 5, md: 6 }
								}
							: {})
					}}
				>
					{parseIcon && (
						<Box
							aria-hidden
							sx={{
								color: theme.palette.primary.main,
								lineHeight: 0,
								'& svg [stroke]:not([stroke="none"])': {
									strokeWidth: 1
								}
							}}
						>
							<Icon
								name={parseIcon.trim()}
								active
								size={64}
								customIconColor={{
									bgColor: theme.palette.primary.main,
									glyphColor: glyphColor
								}}
								color={theme.palette.primary.main}
							/>
						</Box>
					)}
					<Typography
						variant="body-sm"
						sx={(t) => ({
							color: 'text.primary',
							textAlign: 'center',
							maxWidth: '100%',
							overflowWrap: 'break-word',
							wordBreak: 'normal',
							hyphens: 'auto',
							WebkitHyphens: 'auto',
							msHyphens: 'auto',
							[t.breakpoints.up('md')]: {
								fontSize: '1.5rem',
								lineHeight: '2rem',
								letterSpacing: '-1px'
							}
						})}
					>
						{title}
					</Typography>
				</Box>

				{/* Hover state: description + CTA */}
				{description && (
					<Box
						className="linkrow-hover"
						sx={{
							position: 'absolute',
							inset: 0,
							px: { xs: 4, md: 5 },
							py: { xs: 4, md: 5 },
							display: { xs: 'none', md: 'flex' },
							flexDirection: 'column',
							opacity: 0,
							transform: 'translateY(10px)',
							transition: 'opacity 0.4s cubic-bezier(0.22, 1, 0.36, 1), transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)'
						}}
					>
						<Typography variant="body-md" sx={{ color: 'text.primary', mb: 3 }}>
							{description}
						</Typography>
						<Box sx={{ alignSelf: 'flex-start' }}>
							<ArrowSlideHoverCta
								label="Mehr erfahren"
								renderAsSpan
								active={isCardHovered}
								color={theme.palette.text.secondary ?? theme.palette.text.primary}
								typographyVariant="body-md"
								fontWeight={500}
							/>
						</Box>
					</Box>
				)}
			</Box>
		</Link>
	);
};

export default LinkRowCard;
